import { parseSteps, serializeSteps, PRESETS, type Step } from '@eip/engine';
import type { Settings, ViewerPrefs } from './store';
import { defaultSepDefaults, sanitizeSepDefaults, type AppMode, type SepDefaults } from './sep';
import { defaultMockupDefaults, sanitizeMockupDefaults, type MockupDefaults } from './mockup';
import { clampPanelWidth, SESSION_ONLY_SECTIONS } from '../lib/panel';

export interface StorageLike { getItem(k: string): string | null; setItem(k: string, v: string): void }
export const STORAGE_KEY = 'eip.v1';

/** The SettingsBar's DPI field range; a persisted value outside it could never have been typed. */
const DPI_MIN = 72, DPI_MAX = 600;
const HEX_RE = /^#[0-9a-f]{6}$/i;
const VIEWS: ReadonlyArray<string> = ['output', 'source'];
const BACKGROUNDS: ReadonlyArray<string> = ['checker', 'garment', 'white'];

const isFinite_ = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * Keeps only the settings fields that have a usable value, per field, so one
 * bad entry falls back to its default instead of breaking the app: an unknown
 * `presetId` makes `getPreset` throw inside the worker's `inspect` (every run
 * then ends "unknown preset …" with zero checks) while the settings bar's
 * `<select>` still shows the first preset; a non-string `garmentHex` throws in
 * `exportFileName`; a non-numeric `dpi` reaches the resize step as NaN.
 */
export function sanitizeSettings(raw: unknown): Partial<Settings> {
  if (!raw || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;
  const out: Partial<Settings> = {};
  if (typeof r.garmentHex === 'string' && HEX_RE.test(r.garmentHex)) out.garmentHex = r.garmentHex;
  if (isFinite_(r.dpi) && r.dpi >= DPI_MIN && r.dpi <= DPI_MAX) out.dpi = r.dpi;
  // null is a real value here ("native size"), distinct from "absent".
  if (r.targetWidthIn === null) out.targetWidthIn = null;
  else if (isFinite_(r.targetWidthIn) && r.targetWidthIn > 0) out.targetWidthIn = r.targetWidthIn;
  if (typeof r.presetId === 'string' && PRESETS.some((p) => p.id === r.presetId)) out.presetId = r.presetId;
  return out;
}

/**
 * Same per-field rule for the viewer prefs: an unknown `view` blanks the
 * viewer (nothing matches 'output'/'source'), an unknown `background` paints
 * nothing behind the art. `overlays` is never restored — the toggles are
 * per-session UI state and `savePersisted` writes them empty anyway.
 *
 * `panelWidth` is clamped rather than range-checked (a 5000 from a since-shrunk
 * monitor is a usable intent, not corruption) but a non-number is dropped so
 * the store's own default stands; the window's own cap is applied again by
 * `setPanelWidth` on mount, which is the only place that knows the viewport.
 * `collapsed` keeps only its boolean entries, so one junk value cannot take the
 * rest of a shop's folded sections with it.
 */
export function sanitizeViewer(raw: unknown): Partial<ViewerPrefs> {
  if (!raw || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;
  const out: Partial<ViewerPrefs> = {};
  if (typeof r.view === 'string' && VIEWS.includes(r.view)) out.view = r.view as ViewerPrefs['view'];
  if (typeof r.background === 'string' && BACKGROUNDS.includes(r.background)) out.background = r.background as ViewerPrefs['background'];
  if (isFinite_(r.panelWidth)) out.panelWidth = clampPanelWidth(r.panelWidth, Number.POSITIVE_INFINITY);
  if (r.collapsed && typeof r.collapsed === 'object' && !Array.isArray(r.collapsed)) {
    const collapsed: Record<string, boolean> = {};
    for (const [id, v] of Object.entries(r.collapsed as Record<string, unknown>)) if (typeof v === 'boolean') collapsed[id] = v;
    if (Object.keys(collapsed).length) out.collapsed = collapsed;
  }
  return out;
}

/**
 * `mode`, `sep` and `mockup` follow the same per-field, omit-when-absent rule as
 * `settings`/`viewer`/`steps`: the caller (session.ts) only overwrites the
 * store when a field actually validated, so an untouched or corrupt entry
 * leaves the store's own default (`'dtf'`, `defaultSepDefaults()`,
 * `defaultMockupDefaults()`) in place rather than this function re-asserting it.
 * `sep.defaults` and `mockup.defaults` are themselves never partial once present
 * — their sanitizers resolve every field individually, falling back
 * field-by-field rather than all-or-nothing. A payload written before mockup
 * mode existed simply has no `mockup` key, and reloads unchanged.
 */
export function loadPersisted(storage: StorageLike | null): { settings?: Partial<Settings>; steps?: Step[]; viewer?: Partial<ViewerPrefs>; mode?: AppMode; sep?: { defaults: SepDefaults }; mockup?: { defaults: MockupDefaults } } {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw) as { settings?: unknown; steps?: unknown; viewer?: unknown; mode?: unknown; sep?: { defaults?: unknown }; mockup?: { defaults?: unknown } };
    const out: ReturnType<typeof loadPersisted> = {};
    const settings = sanitizeSettings(obj.settings);
    if (Object.keys(settings).length) out.settings = settings;
    const viewer = sanitizeViewer(obj.viewer);
    if (Object.keys(viewer).length) out.viewer = viewer;
    if (obj.steps) { try { out.steps = parseSteps(JSON.stringify(obj.steps)); } catch { /* corrupt steps: fall back to defaults */ } }
    if (obj.mode === 'dtf' || obj.mode === 'sep' || obj.mode === 'mockup') out.mode = obj.mode;
    if (obj.sep) out.sep = { defaults: sanitizeSepDefaults(obj.sep.defaults) };
    if (obj.mockup) out.mockup = { defaults: sanitizeMockupDefaults(obj.mockup.defaults) };
    return out;
  } catch { return {}; }
}

/** The folded sections worth remembering: every one but the session-only ids (see
 *  `SESSION_ONLY_SECTIONS`). Returns the same object when there is nothing to drop,
 *  so the common case allocates nothing. */
function savedCollapsed(collapsed: Record<string, boolean>): Record<string, boolean> {
  if (!SESSION_ONLY_SECTIONS.some((id) => id in collapsed)) return collapsed;
  return Object.fromEntries(Object.entries(collapsed).filter(([id]) => !SESSION_ONLY_SECTIONS.includes(id)));
}

export function savePersisted(storage: StorageLike | null, data: { settings: Settings; steps: Step[]; viewer: ViewerPrefs; mode?: AppMode; sep?: { defaults: SepDefaults }; mockup?: { defaults: MockupDefaults } }): void {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify({
      settings: data.settings,
      steps: JSON.parse(serializeSteps(data.steps)),
      viewer: { background: data.viewer.background, view: data.viewer.view, overlays: {}, panelWidth: data.viewer.panelWidth, collapsed: savedCollapsed(data.viewer.collapsed) },
      mode: data.mode ?? 'dtf',
      sep: { defaults: data.sep?.defaults ?? defaultSepDefaults() },
      mockup: { defaults: data.mockup?.defaults ?? defaultMockupDefaults() },
    }));
  } catch { /* storage unavailable or full: ignore */ }
}
